// Como as balanças de checkout respondem pelo cabo serial (RS-232 ou USB que
// vira porta COM). Pesquisa de 26/10/2026 (fórum do ACBr, manuais Toledo):
//
// - Pedido e resposta (Toledo Prix 3 Prt/"protocolo B", Filizola, Elgin DP,
//   Micheletti): o computador manda ENQ (0x05) e a balança devolve
//   STX + peso + ETX. O peso vem sem vírgula, em gramas ("00446" = 0,446 kg);
//   "IIIII" = peso instável, "NNNNN" = negativo, "SSSSS" = sobrecarga.
// - Contínuo Toledo P03 (indicadores 9091/2180): a balança manda sem parar
//   STX + 3 bytes de estado + 6 dígitos de peso + 6 de tara + CR (+ checksum).
// - Urano e outras mandam o peso escrito ("0,446 kg"): vale o número com vírgula.
//
// Tudo aqui é puro (sem porta nem tela) para os testes cobrirem cada formato.

export const ENQ = 0x05;
const STX = 0x02;
const ETX = 0x03;
const CR = 0x0d;
const LF = 0x0a;

// Ponto de partida de cada marca; o botão "Detectar automaticamente" acha o
// certo testando as combinações (os relatos de campo divergem até no mesmo modelo).
export const MARCAS = [
  { id: "toledo", nome: "Toledo", modelos: "Prix 3, Prix 4, 9094", baudRate: 4800, dataBits: 8, parity: "none", stopBits: 1, pedirPeso: true },
  { id: "filizola", nome: "Filizola", modelos: "CS15, Platina, BP", baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1, pedirPeso: true },
  { id: "urano", nome: "Urano", modelos: "POP-S, POP-Z, US", baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1, pedirPeso: true },
  { id: "elgin", nome: "Elgin", modelos: "DP, SM100", baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1, pedirPeso: true },
  { id: "micheletti", nome: "Micheletti", modelos: "MIC", baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1, pedirPeso: true },
  { id: "outra", nome: "Outra marca", modelos: "", baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1, pedirPeso: true },
];

export const marcaPorId = (id) => MARCAS.find((m) => m.id === id) || MARCAS[MARCAS.length - 1];

// Ordem das tentativas da detecção automática: as mais comuns primeiro.
export const COMBINACOES = [
  { baudRate: 9600, dataBits: 8, parity: "none" },
  { baudRate: 4800, dataBits: 8, parity: "none" },
  { baudRate: 2400, dataBits: 8, parity: "none" },
  { baudRate: 9600, dataBits: 8, parity: "even" },
  { baudRate: 9600, dataBits: 7, parity: "even" },
  { baudRate: 4800, dataBits: 7, parity: "even" },
  { baudRate: 2400, dataBits: 7, parity: "even" },
  { baudRate: 19200, dataBits: 8, parity: "none" },
  { baudRate: 4800, dataBits: 8, parity: "even" },
  { baudRate: 2400, dataBits: 8, parity: "even" },
].map((c) => ({ ...c, stopBits: 1 }));

/** Separa os quadros completos do que chegou e devolve o resto (incompleto). */
export function separarQuadros(bytes) {
  const quadros = [];
  let inicio = 0;
  let i = 0;
  while (i < bytes.length) {
    const b = bytes[i];
    if (b === STX) {
      // P03 contínuo: STX + 3 estados + 12 dígitos + CR, e um byte de checksum
      if (i + 17 <= bytes.length && ehP03(bytes.slice(i, i + 17))) {
        quadros.push(bytes.slice(i, i + 17));
        i += 17;
        if (i < bytes.length && bytes[i] !== STX) i += 1; // pula o checksum
        inicio = i;
        continue;
      }
      inicio = i;
    } else if (b === ETX || b === CR || b === LF) {
      const q = bytes.slice(inicio, i + 1);
      if (q.some((x) => x !== STX && x !== ETX && x !== CR && x !== LF)) quadros.push(q);
      inicio = i + 1;
    }
    i += 1;
  }
  return { quadros, resto: bytes.slice(inicio) };
}

function ehP03(q) {
  if (q.length < 17 || q[0] !== STX || q[16] !== CR) return false;
  for (let k = 4; k < 16; k += 1) if (q[k] < 0x30 || q[k] > 0x39) return false;
  return true;
}

const texto = (bytes) => String.fromCharCode(...bytes.filter((b) => b >= 0x20 && b < 0x7f));

/**
 * Um quadro → leitura. `casas` = casas decimais quando o peso vem sem vírgula
 * (3 = gramas, o padrão das balanças de checkout).
 * Estados: "ok" (peso válido), "vazio" (zero), "instavel", "negativo",
 * "sobrecarga", "invalido" (não deu para entender).
 */
export function interpretar(bytes, { casas = 3 } = {}) {
  if (!bytes || !bytes.length) return { estado: "invalido", kg: null };
  if (ehP03(bytes)) return interpretarP03(bytes);
  const t = texto(bytes).trim();
  if (!t) return { estado: "invalido", kg: null };
  if (/I{3,}/.test(t)) return { estado: "instavel", kg: null };
  if (/N{3,}/.test(t)) return { estado: "negativo", kg: null };
  if (/S{3,}/.test(t)) return { estado: "sobrecarga", kg: null };
  const m = t.match(/(-)?\s*(\d+(?:[.,]\d+)?)/);
  if (!m) return { estado: "invalido", kg: null };
  const numero = m[2];
  let kg = /[.,]/.test(numero)
    ? parseFloat(numero.replace(",", "."))
    : parseInt(numero, 10) / 10 ** casas;
  // Peso escrito em gramas ("446 g")
  if (/[.,]/.test(numero) === false && /\d\s*g\b/i.test(t) && !/kg/i.test(t)) kg = parseInt(numero, 10) / 1000;
  if (!Number.isFinite(kg)) return { estado: "invalido", kg: null };
  kg = Math.round(kg * 1000) / 1000;
  if (m[1]) return { estado: "negativo", kg: null };
  if (kg === 0) return { estado: "vazio", kg: 0 };
  return { estado: "ok", kg };
}

// Toledo P03: estado A traz a posição da vírgula (bits 0-2); estado B traz
// sinal (bit 1), fora da faixa (bit 2) e em movimento (bit 3).
const DIVISOR_P03 = { 0: 0.01, 1: 0.1, 2: 1, 3: 10, 4: 100, 5: 1000, 6: 10000 };

function interpretarP03(q) {
  const a = q[1];
  const b = q[2];
  const peso = parseInt(String.fromCharCode(...q.slice(4, 10)), 10);
  if (b & 0x04) return { estado: "sobrecarga", kg: null };
  if (b & 0x02) return { estado: "negativo", kg: null };
  if (b & 0x08) return { estado: "instavel", kg: null };
  const divisor = DIVISOR_P03[a & 0x07] ?? 1000;
  const kg = Math.round((peso / divisor) * 1000) / 1000;
  return kg === 0 ? { estado: "vazio", kg: 0 } : { estado: "ok", kg };
}

/** Último quadro que dá para entender (a balança contínua manda vários). */
export function ultimaLeitura(quadros, opcoes) {
  for (let i = quadros.length - 1; i >= 0; i -= 1) {
    const l = interpretar(quadros[i], opcoes);
    if (l.estado !== "invalido") return l;
  }
  return null;
}

/**
 * Peso parado: as leituras dos últimos `janelaMs` ficaram dentro de
 * `toleranciaKg` (2 g). Precisa de pelo menos 3 leituras na janela.
 */
export function pesoEstavel(historico, { janelaMs = 900, toleranciaKg = 0.002, agora = Date.now() } = {}) {
  const recentes = historico.filter((h) => agora - h.em <= janelaMs + 400);
  if (recentes.length < 3) return null;
  if (agora - recentes[0].em < janelaMs * 0.66) return null;
  if (recentes.some((h) => h.estado !== "ok")) return null;
  const pesos = recentes.map((h) => h.kg);
  if (Math.max(...pesos) - Math.min(...pesos) > toleranciaKg) return null;
  return recentes[recentes.length - 1].kg;
}

/** Peso que vai para o item: o lido menos o recipiente (tara do produto, em gramas). */
export function pesoLiquido(kg, taraGramas = 0) {
  const liquido = Math.round((kg - (Number(taraGramas) || 0) / 1000) * 1000) / 1000;
  return liquido > 0 ? liquido : 0;
}

export const MENSAGEM_ESTADO = {
  vazio: "Balança vazia",
  instavel: "Aguardando o peso firmar…",
  negativo: "Peso negativo — zere a balança",
  sobrecarga: "Peso acima do limite da balança",
  invalido: "A balança respondeu algo que não deu para ler",
};
